import { useCallback, useMemo, useState } from 'react';

import { useServiceAuditReport } from '@commenda/api/agent-platform/service-auditor/useServiceAuditReport';
import { useServiceAuditsForStep } from '@commenda/api/agent-platform/service-auditor/useServiceAuditsForStep';
import { TaskParty } from '@prisma/client';

import { ServiceAuditReportHelper } from '../helpers/ServiceAuditReportHelper';
import { ServiceAuditRunStatusHelper } from '../helpers/ServiceAuditRunStatusHelper';

interface IUseServiceAuditReportSheetParams {
  isOpen: boolean;
  role: TaskParty;
  workflowExecutionId: string | null | undefined;
  auditorStepId: string | null | undefined;
}

/** The report shown in the sheet: the newest completed audit by default, or the one picked from the list. */
export function useServiceAuditReportSheet({
  isOpen,
  role,
  workflowExecutionId,
  auditorStepId,
}: Readonly<IUseServiceAuditReportSheetParams>) {
  const canSeeAuditDetails =
    ServiceAuditRunStatusHelper.canSeeAuditDetails(role);
  const isReportEnabled = isOpen && canSeeAuditDetails;
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const {
    audits,
    isLoading: isAuditListLoading,
    error: auditListError,
  } = useServiceAuditsForStep(
    { workflowExecutionId, stepId: auditorStepId, enabled: isReportEnabled },
    { refreshIntervalMs: 0 }
  );
  const completedAudits = useMemo(
    () => ServiceAuditReportHelper.getCompletedAudits(audits),
    [audits]
  );
  const auditOptions = useMemo(
    () => ServiceAuditReportHelper.getAuditOptions(completedAudits),
    [completedAudits]
  );
  const latestRunId = completedAudits[0]?.runId ?? null;
  const runId = selectedRunId ?? latestRunId;
  const {
    report,
    isLoading: isReportLoading,
    error: reportError,
    mutate: mutateReport,
  } = useServiceAuditReport({ runId, enabled: isReportEnabled });

  const clearSelectedRun = useCallback(() => setSelectedRunId(null), []);

  return {
    canSeeAuditDetails,
    auditOptions,
    runId,
    selectRun: setSelectedRunId,
    clearSelectedRun,
    report,
    mutateReport,
    isFeedbackOpen: ServiceAuditReportHelper.isFeedbackOpenForRun(
      runId,
      audits
    ),
    isReportPending: isAuditListLoading || isReportLoading,
    isReportFailed: !!auditListError || !!reportError || !runId,
  };
}
